import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Badge, Spinner } from 'react-bootstrap';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import { useAuth } from '../context/AuthContext';
import { getEmployeeByEmail } from '../services/firebase/employeeService';
import { FaUserCircle, FaEnvelope, FaBriefcase, FaBuilding, FaCalendarAlt } from 'react-icons/fa';
import '../assets/css/employee.css';

const ProfilePage = () => {
    const { user } = useAuth();
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchEmployeeData = async () => {
            if (user?.email) {
                try {
                    const response = await getEmployeeByEmail(user.email);
                    if (response.success && response.data) {
                        console.log('[ProfilePage] Employee data loaded:', response.data);
                        setEmployee(response.data);
                    }
                } catch (error) {
                    console.error('[ProfilePage] Error fetching employee:', error);
                }
            }
            setLoading(false);
        };
        fetchEmployeeData();
    }, [user]);
    
    // Format joining date
    const formatDate = (date) => {
        if (!date) return 'Not Available';
        try {
            // Handle Firestore Timestamp
            if (date.seconds) {
                return new Date(date.seconds * 1000).toLocaleDateString('en-IN');
            }
            // Handle string date
            if (typeof date === 'string') {
                const parsedDate = new Date(date);
                if (!isNaN(parsedDate.getTime())) {
                    return parsedDate.toLocaleDateString('en-IN');
                }
            }
            return 'Not Available';
        } catch {
            return 'Not Available';
        }
    };
    
    // Get display values - prefer employee data, fallback to user data
    const displayName = employee?.personal?.fullName || 
        `${employee?.personal?.firstName || user?.firstName || ''} ${employee?.personal?.lastName || user?.lastName || ''}`.trim() ||
        user?.employeeName || user?.email;
    const displayDesignation = employee?.job?.designation || user?.designation || 'Not Assigned';
    const displayDepartment = employee?.job?.department || user?.department || 'Not Assigned';
    const displayJoiningDate = employee?.job?.joiningDate || user?.joiningDate;
    const displayEmail = employee?.personal?.email || user?.email;
    const displayPhoto = employee?.personal?.photo || 
                         employee?.photoURL || 
                         employee?.photo || 
                         employee?.personalInfo?.photo || 
                         user?.photoURL || 
                         user?.photo || 
                         user?.personal?.photo || 
                         '';

    if (loading) {
        return (
            <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
                <Sidebar />
                <div className="flex-grow-1 profile-main-content">
                    <Navbar />
                    <Container fluid className="py-3 py-md-4 px-2 px-md-4">
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Loading profile...</p>
                        </div>
                    </Container>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
            <Sidebar />
            <div className="flex-grow-1 profile-main-content">
                <Navbar />
                <Container fluid className="py-3 py-md-4 px-2 px-md-4">
                    <h2 className="fw-bold text-dark mb-3 mb-md-4 fs-4 fs-md-2">My Profile</h2>

                    <Row className="justify-content-center">
                        <Col xs={12} md={10} lg={8}>
                            <Card className="shadow-sm border-0 rounded-4">
                                <Card.Body className="p-3 p-md-5 text-center">
                                    <div className="mb-4 d-flex justify-content-center">
                                        {displayPhoto ? (
                                            <img
                                                src={displayPhoto}
                                                alt={displayName}
                                                className="rounded-circle shadow"
                                                style={{ width: '110px', height: '110px', objectFit: 'cover', border: '4px solid #2563eb' }}
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ) : (
                                            <FaUserCircle size={90} className="text-primary" />
                                        )}
                                    </div>
                                    <h3 className="fw-bold mb-1 fs-4 fs-md-3">{displayName}</h3>
                                    <p className="text-muted mb-3 small text-break">{displayEmail}</p>
                                    <Badge bg="primary" className="px-3 py-2 mb-4 fs-6 text-uppercase">
                                        {user?.role}
                                    </Badge>

                                    <div className="text-start mt-4">
                                        <h5 className="border-bottom pb-2 mb-3 fs-5">Employment Details</h5>
                                        <Row className="g-2 g-md-3">
                                            <Col xs={12} sm={6}>
                                                <div className="d-flex align-items-center p-2 rounded bg-light bg-opacity-50">
                                                    <div className="bg-white p-2 rounded shadow-sm me-3">
                                                        <FaBriefcase className="text-primary" size={18} />
                                                    </div>
                                                    <div>
                                                        <small className="text-muted d-block" style={{ fontSize: '0.72rem' }}>Designation</small>
                                                        <strong className="fs-6">{displayDesignation}</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={12} sm={6}>
                                                <div className="d-flex align-items-center p-2 rounded bg-light bg-opacity-50">
                                                    <div className="bg-white p-2 rounded shadow-sm me-3">
                                                        <FaBuilding className="text-primary" size={18} />
                                                    </div>
                                                    <div>
                                                        <small className="text-muted d-block" style={{ fontSize: '0.72rem' }}>Department</small>
                                                        <strong className="fs-6">{displayDepartment}</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={12} sm={6}>
                                                <div className="d-flex align-items-center p-2 rounded bg-light bg-opacity-50">
                                                    <div className="bg-white p-2 rounded shadow-sm me-3">
                                                        <FaCalendarAlt className="text-primary" size={18} />
                                                    </div>
                                                    <div>
                                                        <small className="text-muted d-block" style={{ fontSize: '0.72rem' }}>Joining Date</small>
                                                        <strong className="fs-6">{formatDate(displayJoiningDate)}</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={12} sm={6}>
                                                <div className="d-flex align-items-center p-2 rounded bg-light bg-opacity-50">
                                                    <div className="bg-white p-2 rounded shadow-sm me-3">
                                                        <FaEnvelope className="text-primary" size={18} />
                                                    </div>
                                                    <div>
                                                        <small className="text-muted d-block" style={{ fontSize: '0.72rem' }}>Email</small>
                                                        <strong className="fs-6 text-break">{displayEmail}</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
};

export default ProfilePage;
